import { apiClient } from './client';

export type TemplateType =
    | 'Financial Insights'
    | 'Decision Support'
    | 'Operational Efficiency'
    | 'Customer Behavior'
    | 'Predictive Analytics'
    | 'Risk Management'
    | 'Other';

export const TEMPLATE_TYPES: TemplateType[] = [
    'Financial Insights',
    'Decision Support',
    'Operational Efficiency',
    'Customer Behavior',
    'Predictive Analytics',
    'Risk Management',
    'Other'
];

export interface BoardTemplate {
    id: string;
    name: string;
    description?: string;
    templateType: TemplateType;
    chartConfig: any[];
    isPublic: boolean;
    userId?: string;
    createdAt: string;
    updatedAt: string;
    user?: {
        id: string;
        name: string;
        email: string;
    };
}

export interface CreateTemplateRequest {
    name: string;
    description?: string;
    templateType?: TemplateType;
    chartConfig: any[];
    isPublic?: boolean;
}

export interface UpdateTemplateRequest {
    name?: string;
    description?: string;
    templateType?: TemplateType;
    chartConfig?: any[];
    isPublic?: boolean;
}

class BoardTemplateService {
    async getTemplates(): Promise<BoardTemplate[]> {
        const response = await apiClient.get('/board-templates');
        return response.data;
    }

    async getTemplate(id: string): Promise<BoardTemplate> {
        const response = await apiClient.get(`/board-templates/${id}`);
        return response.data;
    }

    async createTemplate(data: CreateTemplateRequest): Promise<BoardTemplate> {
        const response = await apiClient.post('/board-templates', data);
        return response.data;
    }

    async updateTemplate(id: string, data: UpdateTemplateRequest): Promise<BoardTemplate> {
        const response = await apiClient.put(`/board-templates/${id}`, data);
        return response.data;
    }

    async deleteTemplate(id: string): Promise<void> {
        await apiClient.delete(`/board-templates/${id}`);
    }

    async exportTemplate(id: string): Promise<void> {
        // For file download, we need to handle it differently than JSON response
        // We'll create a link element and click it
        const response = await apiClient.get(`/board-templates/${id}/export`, {
            responseType: 'blob'
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Extract filename from header if possible, otherwise use default
        const contentDisposition = response.headers['content-disposition'];
        let filename = `template-${id}.json`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
            if (filenameMatch && filenameMatch.length === 2) {
                filename = filenameMatch[1];
            }
        }

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    async importTemplate(file: File): Promise<BoardTemplate> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const content = e.target?.result as string;
                    const data = JSON.parse(content);

                    const response = await apiClient.post('/board-templates/import', data);
                    resolve(response.data);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsText(file);
        });
    }
}

export const boardTemplateService = new BoardTemplateService();
